import {
  formatDeliveryAreaBlock,
  formatDeliveryCostBlock,
  formatDeliveryZonesListBlock,
  hasDeliveryZonesList,
  looksLikeServiceTypesQuestion,
  matchesCoreDeliveryZonesListIntent,
} from '@/lib/assistant/assistantBusinessFaq';

function prepMinutes(business) {
  const p = business?.preparation_time_minutes;
  return typeof p === 'number' && p > 0 ? p : null;
}

function deliveryMinutes(business) {
  const d = business?.delivery_time_minutes;
  return typeof d === 'number' && d > 0 ? d : null;
}

function leadMinutesForService(business, service) {
  const prep = prepMinutes(business);
  const del = deliveryMinutes(business);
  if (service === 'delivery') {
    if (prep != null && del != null) return Math.max(prep, del);
    return del ?? prep;
  }
  return prep ?? del;
}

function parseRequestedMinutesFromText(n) {
  let m = n.match(/\b(en|para)\s+(\d{1,2})\s*(min|minutos?)\b/);
  if (m) {
    const num = Number(m[2]);
    if (Number.isFinite(num) && num > 0 && num < 300) return num;
  }
  m = n.match(/\b(\d{1,2})\s*(min|minutos?)\b/);
  if (m) {
    const num = Number(m[1]);
    if (Number.isFinite(num) && num > 0 && num < 300) return num;
  }
  if (/\b(llega|llegar|llegue|demora|demoran)\b/.test(n)) {
    m = n.match(/\ben\s+(\d{1,2})(?:\s*(?:min|minutos?))?\b/);
    if (m) {
      const num = Number(m[1]);
      if (Number.isFinite(num) && num > 0 && num < 300) return num;
    }
  }
  return null;
}

function detectUrgencyFastIntent(n) {
  return (
    /\b(r[aá]pido|rapido|urgente|al\s+tiro|altiro|ya\s+mismo|lo\s+antes\s+posible|apuro)\b/.test(n) &&
    !/\b(a las|para las|horas?)\b/.test(n)
  );
}

function detectDesiredScheduleIntent(n, trimmed) {
  if (/\b(mas tarde|más tarde|despu[eé]s|después|recién|recien)\b/.test(n)) return true;
  if (/\b(para las|pa las|a las)\s*\d{1,2}\b/.test(n)) return true;
  if (/\b(para las|pa las|a las)\s*\d{1,2}\s*[.:h]\s*\d{2}\b/.test(n)) return true;
  if (/\d{1,2}\s*[:.]\s*\d{2}\b/.test(trimmed)) return true;
  if (/\blo\s+quiero\s+para\b/.test(n)) return true;
  return false;
}

export function detectGeneralArrivalTimeQuestion(n) {
  if (/\b(cobertura|comuna|barrio|zona|sector|mandan\s+a)\b/.test(n)) return false;
  if (/\b(llega|llegar|lleguen)\b/.test(n) && /\b(a las|para las|esa hora|horas?|hora)\b/.test(n)) return true;
  if (/\b(me\s+)?puede\s+llegar\b/.test(n) && /\b(esa hora|a las|horas?)\b/.test(n)) return true;
  if (/\b(llega|llegar)\b/.test(n) && /\d{1,2}\s*[:.]\s*\d{2}\b/.test(n)) return true;
  return false;
}

function serviceTypesList(business) {
  return business?.service_types || [];
}

export function answerServiceTypesQuestion(business, n) {
  const st = business.service_types || [];
  const hasDelivery = st.includes('delivery');
  const hasRetiro = st.includes('retiro');
  const hasMesa = st.includes('mesa') && business.has_tables === true;
  const bk = business.business_type === 'dark_kitchen';

  const asksDelivery =
    /\b(delivery|domicilio|envio|envío|reparto)\b/.test(n) ||
    /\b(tienen\s+delivery|hay\s+delivery|hacen\s+delivery)\b/.test(n);
  const asksRetiro =
    /\b(retiro|retirar|para\s+llevar|take\s*away)\b/.test(n) ||
    /\b(puedo\s+retirar|se\s+puede\s+retirar|pasar\s+a\s+buscar)\b/.test(n) ||
    /\b(pedir\s+para\s+llevar)\b/.test(n) ||
    /\b(puedo\s+ir|voy a buscar|ir a buscar|ir a retir|recoger|recojo|buscar (el |mi )?pedido|paso a buscar|levantar (el |mi )?pedido)\b/.test(
      n
    ) ||
    /\b(ir\s+al\s+local|pasar\s+por\s+el\s+local)\b/.test(n);
  const asksLocal =
    /\b(mesa|en\s+el\s+local|atienden\s+en)\b/.test(n) ||
    /\b(comer\s+(ahi|ahí|allá|all[ií]|aca|acá))\b/.test(n) ||
    /\b(puedo\s+comer|se\s+puede\s+comer)\b/.test(n);
  const asksSoloDelivery = /\b(es\s+)?(solo|s[oó]lo)\s+delivery\b/.test(n);

  if (asksSoloDelivery) {
    if (bk && hasDelivery && !hasRetiro && !hasMesa) {
      return 'Este local figura como dark kitchen: en la configuración es principalmente delivery.';
    }
    return hasDelivery
      ? 'Sí: en la configuración este local hace delivery.'
      : 'No veo delivery habilitado en la configuración de este local.';
  }

  if (asksDelivery && !asksRetiro && !asksLocal) {
    return hasDelivery
      ? 'Sí, este local tiene delivery habilitado en la configuración.'
      : 'No veo delivery habilitado en este local.';
  }

  if (asksRetiro && !asksDelivery && !asksLocal) {
    const addr = business?.address && String(business.address).trim();
    if (hasRetiro) {
      if (addr) {
        return `Sí, podés retirar tu pedido en el local 😊 Dirección: ${addr}. Si no te queda claro, pedí el mapa por WhatsApp.`;
      }
      return 'Sí, podés retirar tu pedido en el local según la configuración. La dire te la fija el menú o el WhatsApp del local. 😊';
    }
    return 'No veo retiro habilitado en este local.';
  }

  if (asksLocal) {
    if (hasMesa) {
      return 'Sí, este local atiende en el local y tiene mesa habilitada en la configuración.';
    }
    if (st.includes('mesa') && business.has_tables !== true) {
      return 'Figura “mesa” entre servicios, pero el local no tiene mesas habilitadas en la configuración.';
    }
    if (bk) {
      return 'Este local es dark kitchen: no figura atención con mesa en el local.';
    }
    if (hasRetiro && !hasMesa) {
      return 'Aquí podés pedir para retiro en el local; no veo mesa para comer ahí habilitada.';
    }
    if (hasDelivery && !hasMesa && !hasRetiro) {
      return 'Según la configuración, este local no muestra retiro ni mesa; sí delivery.';
    }
    return 'No veo atención en el local / mesa habilitada en la configuración.';
  }

  const parts = [];
  if (hasDelivery) parts.push('delivery');
  if (hasRetiro) parts.push('retiro en el local');
  if (hasMesa) parts.push('mesa en el local');

  if (!parts.length) {
    return 'No veo tipos de servicio (delivery, retiro o mesa) cargados en la configuración.';
  }

  if (parts.length === 1) {
    return `Este local tiene habilitado: ${parts[0]}.`;
  }
  return `Este local tiene habilitado: ${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}.`;
}

export function answerTimingAndSchedulingIntents(
  n,
  trimmed,
  business,
  defaultFallback,
  userRaw,
  servicePreference = null
) {
  const st = serviceTypesList(business);
  const hasDel = st.includes('delivery');
  const hasRet = st.includes('retiro');

  const wantDel = servicePreference === 'delivery';
  const wantPickup = servicePreference === 'pickup';
  const includeDel = hasDel && (wantDel || servicePreference == null);
  const includePickup = hasRet && (wantPickup || servicePreference == null);

  const reqMin = parseRequestedMinutesFromText(n);
  const urgent = detectUrgencyFastIntent(n);
  const schedule = detectDesiredScheduleIntent(n, trimmed);
  const arrivalQ = detectGeneralArrivalTimeQuestion(n);

  if (reqMin != null && (urgent || /\b(min|minutos?|en\s+\d)\b/.test(n))) {
    const lines = [];
    let anyConfigured = false;

    if (wantDel && !hasDel) {
      return {
        text: 'En la configuración de este local no veo delivery habilitado, así que no puedo darte un tiempo de envío a domicilio desde acá.',
        products: null,
        fallbackKeys: hasRet ? ['order_pickup', 'catalog', 'main'] : ['catalog', 'main'],
        containmentIncluded: true,
        frustrationLevel: 0,
      };
    }
    if (wantPickup && !hasRet) {
      return {
        text: 'En la configuración de este local no veo retiro habilitado, así que no puedo referirme a un tiempo de retiro desde acá.',
        products: null,
        fallbackKeys: hasDel ? ['order_delivery', 'catalog', 'main'] : ['catalog', 'main'],
        containmentIncluded: true,
        frustrationLevel: 0,
      };
    }

    if (includePickup) {
      const m = leadMinutesForService(business, 'retiro');
      if (m != null) {
        anyConfigured = true;
        if (reqMin < m) {
          lines.push(`Para retiro, en la configuración del local el tiempo estimado parte desde ${m} minutos.`);
        } else {
          lines.push(
            `Para retiro figura ~${m} min en la configuración; tu plazo (${reqMin} min) está por sobre ese mínimo referencial.`
          );
        }
      }
    }
    if (includeDel) {
      const m = leadMinutesForService(business, 'delivery');
      if (m != null) {
        anyConfigured = true;
        if (reqMin < m) {
          lines.push(`Para delivery, según lo cargado, el tiempo estimado suele ser desde ~${m} minutos.`);
        } else {
          lines.push(
            `Para delivery figura ~${m} min en la configuración; pedir en ${reqMin} min puede ser viable según cola y distancia (el local confirma).`
          );
        }
      }
    }

    if (!anyConfigured) {
      return {
        text:
          'En la configuración de este local no veo minutos de preparación o delivery cargados, así que no puedo comparar con un número exacto. Al pedir podés dejar tu urgencia en las notas y el local te confirma.',
        products: null,
        fallbackKeys: ['checkout_cart', 'catalog', 'main'],
        orderNoteAppend: userRaw ? `Urgencia indicada (chat): ${String(userRaw).slice(0, 200)}` : null,
        containmentIncluded: true,
        frustrationLevel: 0,
      };
    }
    const tight = lines.some((l) => l.includes('parte desde') || l.includes('suele ser desde'));
    const head = tight ? 'En ese margen estaría muy justo 😕' : 'Perfecto 👌';
    return {
      text: `${head}\n\n${lines.join('\n\n')}`.trim(),
      products: null,
      fallbackKeys: ['checkout_cart', 'catalog', 'main'],
      orderNoteAppend: userRaw ? `Tiempo mencionado (chat): ${String(userRaw).slice(0, 200)}` : null,
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (urgent && reqMin == null) {
    const bits = [];
    if (wantDel && !hasDel) {
      return {
        text: 'No veo delivery habilitado en este local; no puedo referirme a tiempos de envío a domicilio.',
        products: null,
        fallbackKeys: hasRet ? ['order_pickup', 'catalog', 'main'] : ['catalog', 'main'],
        containmentIncluded: true,
        frustrationLevel: 0,
      };
    }
    if (wantPickup && !hasRet) {
      return {
        text: 'No veo retiro habilitado en este local; no puedo referirme a tiempos de retiro.',
        products: null,
        fallbackKeys: hasDel ? ['order_delivery', 'catalog', 'main'] : ['catalog', 'main'],
        containmentIncluded: true,
        frustrationLevel: 0,
      };
    }

    if (includePickup) {
      const m = leadMinutesForService(business, 'retiro');
      if (m != null) bits.push(`Retiro: desde ~${m} min (según lo cargado).`);
    }
    if (includeDel) {
      const m = leadMinutesForService(business, 'delivery');
      if (m != null) bits.push(`Delivery: desde ~${m} min (según lo cargado).`);
    }
    if (!bits.length) {
      return {
        text:
          'No veo tiempos en minutos cargados para este local. Al armar el pedido podés anotar la urgencia en las notas y el local te responde.',
        products: null,
        fallbackKeys: ['checkout_cart', 'catalog', 'main'],
        orderNoteAppend: userRaw ? `Urgencia (chat): ${String(userRaw).slice(0, 200)}` : null,
        containmentIncluded: true,
        frustrationLevel: 0,
      };
    }
    let tail = '';
    if (includeDel && includePickup) {
      tail = '\n\nEl tiempo final depende de la cola y, en delivery, de la distancia.';
    } else if (includeDel) {
      tail = '\n\nEl tiempo final depende de la cola del local y de la distancia.';
    } else {
      tail = '\n\nEl tiempo final depende de la cola del local.';
    }
    return {
      text: `Dale 👌 según la configuración que veo acá:\n\n${bits.join('\n')}${tail}`,
      products: null,
      fallbackKeys: ['checkout_cart', 'catalog', 'main'],
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (schedule) {
    const hint =
      servicePreference === 'pickup'
        ? 'Perfecto 👌 Podés dejar la hora que necesitás en las notas del pedido; el local lo ve al confirmar (retiro o WhatsApp).'
        : servicePreference === 'delivery'
          ? 'Perfecto 👌 Podés dejar la hora que necesitás en las notas del pedido; el local lo ve al confirmar (delivery o WhatsApp).'
          : 'Perfecto 👌 Podés dejar la hora que necesitás y cualquier detalle en las notas del pedido; el local lo ve al confirmar por acá o por WhatsApp.';
    return {
      text: hint,
      products: null,
      fallbackKeys: ['checkout_cart', 'catalog', 'main'],
      orderNoteAppend: userRaw ? `Preferencia de horario (chat): ${String(userRaw).slice(0, 200)}` : null,
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (arrivalQ) {
    const keys = [];
    let text =
      'Depende de la hora, la zona y el tipo de servicio 👀 Al poner tu dirección o elegir retiro vas a ver el tiempo estimado antes de confirmar.';
    if (wantDel && hasDel) {
      text =
        'Depende de la hora y tu zona 👀 Al poner tu dirección para delivery vas a ver el tiempo estimado antes de confirmar.';
      keys.push('order_delivery');
    } else if (wantDel && !hasDel) {
      text =
        'Depende de la hora y la cola del local 👀 Si el local ofrece retiro, al elegirlo vas a ver el tiempo estimado antes de confirmar.';
      if (hasRet) keys.push('order_pickup');
    } else if (wantPickup && hasRet) {
      text =
        'Depende de la hora y la cola del local 👀 Al elegir retiro vas a ver el tiempo estimado antes de confirmar.';
      keys.push('order_pickup');
    } else if (wantPickup && !hasRet) {
      text =
        'Depende de la hora y tu zona 👀 Si el local hace delivery, al poner tu dirección vas a ver el tiempo estimado antes de confirmar.';
      if (hasDel) keys.push('order_delivery');
    } else {
      if (hasDel) keys.push('order_delivery');
      if (hasRet) keys.push('order_pickup');
    }
    keys.push('catalog', 'main');
    return {
      text,
      products: null,
      fallbackKeys: keys.length ? keys : ['order_hint', 'catalog', 'main'],
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  return null;
}

export function looksLikeDeliveryCoverageQuestion(n) {
  if (detectGeneralArrivalTimeQuestion(n)) return false;
  return (
    /\b(llega|llegan|llegas|envian|envían|mandan|cobertura|sector|comuna|barrio|zona)\b/.test(n) ||
    /\b(delivery)\b.*\b(a|hasta|en)\b/.test(n)
  );
}

/** Intents delivery: costo, zonas, servicios, tiempos y cobertura. */
export function tryDeliveryFreeTextIntents(n, trimmed, business, defaultFallback, servicePreference) {
  const looksLikeDeliveryCost =
    /\b(cuanto|cuánto)\b.*\b(delivery|despacho|envio|envío|domicilio|reparto)\b/.test(n) ||
    /\b(precio|costo|valor)\b.*\b(delivery|despacho|envio|envío|domicilio|reparto)\b/.test(n) ||
    /\b(delivery|despacho|envio|envío|domicilio|reparto)\b.*\b(cuesta|sale|precio|cuanto|cuánto|valor)\b/.test(
      n
    ) ||
    /^(envío|envio|despacho|delivery|domicilio|reparto)\s*$/i.test(trimmed);

  if (looksLikeDeliveryCost) {
    if (hasDeliveryZonesList(business)) {
      return { text: formatDeliveryZonesListBlock(business), products: null, fallbackKeys: defaultFallback };
    }
    return { text: formatDeliveryCostBlock(business), products: null, fallbackKeys: defaultFallback };
  }

  if (hasDeliveryZonesList(business) && matchesCoreDeliveryZonesListIntent(n)) {
    return { text: formatDeliveryZonesListBlock(business), products: null, fallbackKeys: defaultFallback };
  }

  if (looksLikeServiceTypesQuestion(n)) {
    return {
      text: answerServiceTypesQuestion(business, n),
      products: null,
      fallbackKeys: defaultFallback,
    };
  }

  const timingAns = answerTimingAndSchedulingIntents(
    n,
    trimmed,
    business,
    defaultFallback,
    trimmed,
    servicePreference
  );
  if (timingAns) return timingAns;

  if (looksLikeDeliveryCoverageQuestion(n)) {
    return { text: formatDeliveryAreaBlock(business), products: null, fallbackKeys: defaultFallback };
  }

  return null;
}
